import { useState } from 'react'
import {
  EPatientStatus,
  TRoomViewPatient,
  TRoomViewRoom,
  TRoomViewScheduledSurgery,
} from '../RoomView.types'
import { useRoomViewStore } from 'store/roomView/roomView.store'
import { getProviderNameWithCredentials } from '../helpers'
import { useUpdatePatientStatusMutation } from '../hooks/surgeries.hook'
import { enqueueSnackbar } from 'notistack'
import { ApolloError } from '@apollo/client'

export const useDischargedPatientsModalLogic = () => {
  const {
    dischargedPatients,
    scheduledSurgeries,
    patientStatusOptions,
    refetchAll,
  } = useRoomViewStore()

  const [updatePatientStatus] = useUpdatePatientStatusMutation()

  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedSurgeryInfo, setSelectedSurgeryInfo] = useState<{
    surgeryId: string
    initialStatus: EPatientStatus
    room: TRoomViewRoom
  } | null>(null)
  const [selectedPatient, setSelectedPatient] =
    useState<TRoomViewPatient | null>(null)

  const getProvider = (patient: TRoomViewPatient) => {
    const provider = getProviderNameWithCredentials(
      scheduledSurgeries.find((surgery) => surgery.patient._id === patient._id)
        ?.attendingProvider
    )
    return provider
  }

  const handleBackToRoom = async (
    event: React.MouseEvent<HTMLElement>,
    surgery: TRoomViewScheduledSurgery | undefined
  ) => {
    event.stopPropagation()
    if (!surgery) {
      enqueueSnackbar('Patient was not assigned to a surgery', {
        variant: 'error',
      })
      return
    }
    const roomId = surgery.patientStatusHistory[1]?.info?.room?.id
    if (!roomId) {
      enqueueSnackbar('Patient was not assigned to a room', {
        variant: 'error',
      })
      return
    }
    try {
      await updatePatientStatus({
        variables: {
          procedureId: surgery._id,
          patientStatus: EPatientStatus.ASSIGN_ROOM,
          roomId,
        },
      })
      enqueueSnackbar('Patient status updated successfully', {
        variant: 'success',
      })
      refetchAll()
    } catch (error) {
      if (error instanceof ApolloError) {
        enqueueSnackbar(error?.graphQLErrors[0].message, {
          variant: 'error',
        })
      } else {
        enqueueSnackbar('Error updating patient status', {
          variant: 'error',
        })
      }
    }
  }

  const getRoomName = (
    patient: TRoomViewPatient
  ): TRoomViewRoom | undefined => {
    return scheduledSurgeries.find(
      (surgery) => surgery.patient._id === patient._id
    )?.patientStatusHistory[1]?.info?.room
  }

  return {
    statusAnchorEl,
    selectedPatient,
    dischargedPatients,
    scheduledSurgeries,
    selectedSurgeryInfo,
    patientStatusOptions,
    getProvider,
    getRoomName,
    handleBackToRoom,
    setStatusAnchorEl,
    setSelectedPatient,
    setSelectedSurgeryInfo,
  }
}
